import values from 'lodash/values'
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'

import type { RouteDef } from '@ztechapps/common.contract'

export const routesDef = {
  OverviewPage: {
    name: 'Overview',
    path: '/app/overview',
    //component: Loadable({ loader: () => import('../view/overview') }),
    exact: true,
    layout: 'main',
  },
  BlogsPage: {
    name: 'Blogs',
    path: '/app/settings/blogs',
    exact: true,
    layout: 'main',
  },
  // COUPLING: the editor path `/app/settings/blogs/:_id` must match the admin link built in libs/ai/node/src/services/tool-function/mcp/docs/SearchBlogsMcpTool.ts
  BlogItemPage: {
    name: 'Blog Item',
    path: '/app/settings/blogs/:_id',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)

      return `/app/settings/blogs/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  BlogPreviewPage: {
    name: 'Blog Preview',
    path: '/blog-preview/:slug',
    altPath: ({ slug }) => {
      slug = encodeURIComponent(slug)

      return `/blog-preview/${slug}`
    },
    exact: true,
    layout: 'blank',
  },

  ProductListPage: {
    name: 'Products',
    path: '/app/products/items',
    altPath: () => {
      return '/app/products/items'
    },
    exact: true,
    layout: 'main',
  },
  ProductItemPage: {
    name: 'Product',
    path: '/app/products/items/item/:_id',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)

      return `/app/products/items/item/${_id}`
    },
    exact: true,
    layout: 'main',
  },

  OrdersPage: {
    name: 'Sales',
    path: '/app/orders/sales',
    altPath: () => {
      return '/app/orders/sales'
    },
    //component: Loadable({ loader: () => import('../view/order/sales/list') }),
    exact: true,
    layout: 'main',
  },
  OrderItemPage: {
    name: 'Sale Item',
    path: '/app/orders/sales/detail/:_id/',
    altPath: ({ _id }: { _id: string }) => {
      _id = encodeURIComponent(_id)
      return `/app/orders/sales/detail/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  OrderItemPaymentSuccessPage: {
    name: 'Sale  Item',
    path: '/app/orders/sales/detail/:_id/success',
    altPath: ({ _id }: { _id: string }) => {
      _id = encodeURIComponent(_id)
      return `/app/orders/sales/detail/${_id}/success`
    },
    exact: true,
    layout: 'main',
  },

  OrderItemPaymentFailedPage: {
    name: 'Sale  Item',
    path: '/app/orders/sales/detail/:_id/failed',
    altPath: ({ _id }: { _id: string }) => {
      _id = encodeURIComponent(_id)
      return `/app/orders/sales/detail/${_id}/failed`
    },
    exact: true,
    layout: 'main',
  },

  //Production routes
  ProductionPage: {
    name: 'Production',
    path: '/app/orders/production',
    exact: true,
    layout: 'main',
  },
  ProductionItemPage: {
    name: 'Production Item',
    path: '/app/orders/production/detail/:_id',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/app/orders/production/detail/${_id}`
    },
    exact: true,
    layout: 'main',
  },

  AdjustmentPage: {
    name: 'Adjustment',
    path: '/app/adjustment',
    exact: true,
    layout: 'main',
  },
  AdjustmentItemPage: {
    name: 'Adjustment Item',
    path: '/app/adjustment/detail/:_id',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/app/adjustment/detail/${_id}`
    },
    exact: true,
    layout: 'main',
  },

  ContactsPage: {
    name: 'Companies and Contacts',
    path: '/app/contacts',
    exact: true,
    layout: 'main',
  },

  CustomerItemPage: {
    name: 'Customer Item',
    path: '/app/customer/:_id',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)

      return `/app/customer/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  CompanyItemPage: {
    name: 'Company',
    path: '/app/company/:_id',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)

      return `/app/company/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  VendorItemPage: {
    name: 'Vendor',
    path: '/app/vendor/:_id',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/app/vendor/${_id}`
    },
    exact: true,
    layout: 'main',
  },

  ReportPage: {
    name: 'Reports',
    path: '/app/reports',

    exact: true,
    layout: 'main',
  },
  // COUPLING: this path is copied into apps/panelpunches/api-test/src/browser/support/page-objects/admin/PivotReportPage.ts
  ReportCustomerPage: {
    name: 'Customer Reports',
    path: '/app/reports/customer',
    altPath: ({ templateId }) => {
      const searchParams = new URLSearchParams({ templateId })
      return `/app/reports/customer?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },
  // COUPLING: this path is copied into apps/panelpunches/api-test/src/browser/support/page-objects/admin/PivotReportPage.ts
  ReportOrderPage: {
    name: 'Order Reports',
    path: '/app/reports/order',
    altPath: ({ templateId }) => {
      const searchParams = new URLSearchParams({ templateId })
      return `/app/reports/order?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },
  ReportClosedOrderPage: {
    name: 'Closed Order Reports',
    path: '/app/reports/closedOrder',
    altPath: ({ templateId }) => {
      const searchParams = new URLSearchParams({ templateId })
      return `/app/reports/closedOrder?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },
  ReportArAgingPage: {
    name: 'AR Aging Report',
    path: '/app/reports/arAging',
    // Fixed custom screen — ignores the templateId the report-list action passes.
    altPath: () => '/app/reports/arAging',
    exact: true,
    layout: 'main',
  },
  ReportArAgingCompanyPage: {
    name: 'AR Aging Company',
    path: '/app/reports/arAging/:_id',
    altPath: ({ _id }) => '/app/reports/arAging/' + encodeURIComponent(_id),
    exact: true,
    layout: 'main',
  },
  ReportOpenOrderPage: {
    name: 'Open Order Reports',
    path: '/app/reports/openOrder',
    altPath: ({ templateId }) => {
      const searchParams = new URLSearchParams({ templateId })
      return `/app/reports/openOrder?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },
  // COUPLING: this path is copied into apps/panelpunches/api-test/src/browser/support/page-objects/admin/PivotReportPage.ts
  ReportOrderWithProductsPage: {
    name: 'Order With Products Reports',
    path: '/app/reports/orderWithProducts',
    altPath: ({ templateId }) => {
      const searchParams = new URLSearchParams({ templateId })
      return `/app/reports/orderWithProducts?${searchParams.toString()}`
    },
    layout: 'main',
  },
  // COUPLING: this path is copied into apps/panelpunches/api-test/src/browser/support/page-objects/admin/PivotReportPage.ts
  ReportProductPage: {
    name: 'Product Reports',
    path: '/app/reports/product',
    altPath: ({ templateId }) => {
      const searchParams = new URLSearchParams({ templateId })
      return `/app/reports/product?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },
  // COUPLING: this path is copied into apps/panelpunches/api-test/src/browser/support/page-objects/admin/PivotReportPage.ts
  ReportInventoryPage: {
    name: 'Inventory Reports',
    path: '/app/reports/inventory',
    altPath: ({ templateId }) => {
      const searchParams = new URLSearchParams({ templateId })
      return `/app/reports/inventory?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },
  // COUPLING: this path is copied into apps/panelpunches/api-test/src/browser/support/page-objects/admin/PivotReportPage.ts
  ReportInventoryBySerialNumberPage: {
    name: 'Inventory By Serial Number Reports',
    path: '/app/reports/inventorybyserialnumber',
    altPath: ({ templateId }) => {
      const searchParams = new URLSearchParams({ templateId })
      return `/app/reports/inventorybyserialnumber?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },

  ReportDriverPickupSummaryPage: {
    name: 'Driver Pickup Summary Reports',
    path: '/app/reports/driverPickupSummary',
    altPath: ({ date, orderIds }: { date?: string; orderIds?: string }) => {
      const params = omitBy({ date, orderIds }, isNil)
      const searchParams = new URLSearchParams(params)
      return `/app/reports/driverPickupSummary?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },

  SettingsDetailPage: {
    name: 'Settings',
    path: '/app/settings',
    exact: true,
    layout: 'main',
  },
  AuthSuccessPage: {
    name: 'Auth Success',
    path: '/auth/success/:auth',
    exact: true,
    layout: 'blank',
  },
  AuthFailedPage: {
    name: 'Auth Failed',
    path: '/auth/failed',
    exact: true,
    layout: 'blank',
  },
  LoginPage: {
    name: 'Login',
    path: '/account/login',
    altPath: () => '/account/login',
    //component: Loadable({loader: () => import('../view/account/login/LoginPage'),}),
    exact: true,
    layout: 'blank',
  },
  JoinPage: {
    name: 'Join',
    path: '/account/join/',
    //component: Loadable({loader: () => import('../view/account/join/JoinPage'),}),
    exact: true,
    layout: 'blank',
  },
  LoadingPage: {
    name: 'Loading',
    path: '/',
    //component: Loadable({loader: () => import('../view/account/loading/LoadingPage'),}),
    exact: true,
    layout: 'blank',
  },
  NotificationListPage: {
    name: 'Notification List',
    path: '/app/notifications/list',
    //component: Loadable({ loader: () => import('../view/notification/') }),
    exact: true,
    layout: 'main',
  },
  ChangePasswordPage: {
    name: 'ChangePassword',
    path: '/account/change-password/:username?/:code?',
    altPath: () => '/account/change-password',
    //component: Loadable({ loader: () => import('../view/account/changePassword') }),
    exact: true,
    layout: 'blank',
  },
  ProfilePage: {
    name: 'Profile',
    path: '/account/profile',
    //component: Loadable({ loader: () => import('../view/account/profile') }),
    exact: true,
    layout: 'main',
  },

  //project routes
  ProjectsPage: {
    name: 'Projects',
    path: '/app/projects',
    exact: true,
    layout: 'main',
  },
  ProjectItemPage: {
    name: 'Project Item',
    path: '/app/projects/detail/:_id',
    altPath: ({ _id }) => {
      // const searchParams = new URLSearchParams({ _id })
      _id = encodeURIComponent(_id)
      return `/app/projects/detail/${_id}`
    },
  },

  TaskItemPage: {
    name: 'Task Item',
    path: '/app/tasks/detail/:_id',
    altPath: ({ _id }) => {
      _id = encodeURIComponent(_id)
      return `/app/tasks/detail/${_id}`
    },
    exact: true,
    layout: 'main',
  },
  TaskCalendarPage: {
    name: 'Task Calendar',
    path: '/app/tasks/calendar',
    altPath: ({ _id }) => {
      const searchParams = new URLSearchParams({ _id })
      return `/app/tasks/calendar?${searchParams.toString()}`
    },
    exact: true,
    layout: 'main',
  },
  TaskListPage: {
    name: 'Task List',
    path: '/app/tasks/list',
    exact: true,
    layout: 'main',
  },
  TaskBoardPage: {
    name: 'Task Board',
    path: '/app/tasks/board',
    exact: true,
    layout: 'main',
  },

  ConversationPage: {
    name: 'Conversation',
    path: '/app/conversation',
    altPath: () => '/app/conversation',
    exact: true,
    layout: 'main',
  },
  ConversationItemPage: {
    name: 'Conversation',
    path: '/app/conversation/detail/:_id',
    altPath: ({ _id }: { _id: string }) => {
      _id = encodeURIComponent(_id)
      return `/app/conversation/detail/${_id}`
    },
    exact: true,
    layout: 'main',
  },

  //returns
  ReturnListItemPage: {
    name: 'Returns',
    path: '/app/returns/list-items',
    exact: true,
    layout: 'main',
  },
  ReturnDetailPage: {
    name: 'Return Item',
    path: '/app/returns/detail/:_id',
    altPath: ({ _id }: { _id: string }) => {
      _id = encodeURIComponent(_id)
      return `/app/returns/detail/${_id}`
    },
    exact: true,
    layout: 'main',
  },

  //purchases
  PurchasesPage: {
    name: 'Purchases',
    path: '/app/orders/purchases',
    altPath: () => {
      return '/app/orders/purchases'
    },
    exact: true,
    layout: 'main',
  },
  PurchaseItemPage: {
    name: 'Purchase Item',
    path: '/app/orders/purchases/detail/:_id',
    altPath: ({ _id }: { _id: string }) => {
      _id = encodeURIComponent(_id)
      return `/app/orders/purchases/detail/${_id}/`
    },
    exact: true,
    layout: 'main',
  },

  //logistics
  LogisticListPage: {
    name: 'Logistics',
    path: '/app/logistics/list',
    exact: true,
    layout: 'main',
  },

  LogisticItemPage: {
    name: 'Logistic  Item',
    path: '/app/logistics/list/detail/:_id',
    altPath: ({ _id }: { _id: string }) => {
      _id = encodeURIComponent(_id)
      return `/app/logistics/list/detail/${_id}/`
    },
    exact: true,
    layout: 'main',
  },

  //utilities
  UtilitiesPage: {
    name: 'Utilities Page',
    path: '/app/utilities',
    altPath: () => '/app/utilities',
    exact: true,
    layout: 'main',
  },
  QbCompareProductsPage: {
    name: 'QB Compare Products Page',
    path: '/app/utilities/qb-compare/products',
    altPath: () => '/app/utilities/qb-compare/products',
    exact: true,
    layout: 'main',
  },
} as const satisfies Record<string, RouteDef>

export const routesArray = values(routesDef)
